import { CallStatusEnum, CancelCallTypeStatusEnum } from "../utils/enums";

const baseUrl = import.meta.env.VITE_ZOOM_API_BASE_URL;

interface InviteUserForm {
  callId: string;
  userId: string;
}

const apiUrls = {
  channelDetailsById: ({ callId }: { callId: string }) =>
    `${baseUrl}/${callId}/ws-details`,
  onlineUsers: () => `${baseUrl}/online-users`,
  inviteUser: ({ callId, userId }: InviteUserForm) =>
    `${baseUrl}/${callId}/invite/${userId}`,
  cancelCall: (callId: string) => `${baseUrl}/${callId}`,
  callStatus: (callId: string) => `${baseUrl}/${callId}/status`,
  submitHelpRequest: () => `${baseUrl}/callback`,
  decline: ({ callId, userId }) => `${baseUrl}/${callId}/decline/${userId}`,
  rejectionReason: ({ callId }) => `${baseUrl}/${callId}/reasons`
};

const getAuthHeaders = (token: string | null) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {})
});
export interface ChannelDetailsResponse {
  channel_name: string;
  channel_token: string;
  access_token: string;
  min_wait_time: number;
  max_wait_time: number;
  mobile: string;
  userName: string;
  patient_uuid: string;
  ct_id: string;
}

export const getChannelDetails = async (
  id: string,
  token: string
): Promise<ChannelDetailsResponse | null> => {
  try {
    const response = await fetch(apiUrls.channelDetailsById({ callId: id }), {
      headers: getAuthHeaders(token)
    });
    if (!response.ok) {
      console.error("Failed to fetch channel details");
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching channel details:", error);
    return null;
  }
};

export interface OnlineUser {
  id: string;
  mitelId: string;
  name: string;
  group: string;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

export const getOnlineUsers = async (
  token: string
): Promise<ApiResponse<OnlineUser[]> | null> => {
  try {
    const response = await fetch(apiUrls.onlineUsers(), {
      headers: getAuthHeaders(token)
    });
    if (!response.ok) {
      console.error("Failed to fetch online users");
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching online users:", error);
    return null;
  }
};

let timerId: NodeJS.Timeout;
export const pollOnlineUsers = (
  interval: number,
  callback: (users: ApiResponse<OnlineUser[]> | null) => void,
  token: string
): (() => void) => {
  let retryCount = 0;

  const poll = async () => {
    try {
      const users = await getOnlineUsers(token);
      retryCount = 0; // Reset retry count on success
      callback(users);
    } catch (error) {
      console.error("Polling error:", error);
      retryCount++;
      if (retryCount >= 3) {
        console.error("Max retry attempts reached. Stopping polling.");
        clearInterval(timerId);
      }
    }
  };

  // Perform the first call immediately
  poll();

  // Set up the interval for subsequent polling
  timerId = setInterval(() => {
    if (retryCount < 3) {
      poll();
    }
  }, interval);

  return () => clearInterval(timerId);
};

export interface ChannelDetailsRawResponse {
  channel_name: string;
  channel_token: string;
  access_token: string;
}

export const inviteUser = async (
  data: InviteUserForm,
  token: string
): Promise<ApiResponse<ChannelDetailsRawResponse> | boolean> => {
  try {
    const response = await fetch(apiUrls.inviteUser(data), {
      method: "POST",

      headers: getAuthHeaders(token)
    });
    const resData = await response.json();
    if (!response.ok) {
      console.error("Failed to invite user");
      throw new Error(resData.message || "Failed to invite user");
    }
    return resData;
  } catch (error) {
    console.error("Error inviting user:", error);
    return false;
  }
};

export const cancelCall = async (
  callId: string,
  type: CancelCallTypeStatusEnum,
  token: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(apiUrls.cancelCall(callId), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify({
        reason: type
      })
    });

    const resData = await response.json();

    if (!response.ok) {
      console.error(
        `Error ${response.status}: ${
          resData.message || "Failed to cancel call"
        }`
      );
      return {
        success: false,
        message: resData.message || "Something went wrong. Please try again."
      };
    }

    return {
      success: true,
      message: resData.message || "Call cancelled successfully."
    };
  } catch (error) {
    console.error("Network error cancelling call:", error);
    return {
      success: false,
      message: "Something went wrong. Please try again."
    };
  }
};

export interface HelpRequestPayload {
  call_id: string;
  name: string;
  mobile: string | undefined;
  description: string;
  emergency: boolean;
}

export interface HelpRequestResponse {
  success: boolean;
  message: string;
  data?: {
    ticket_id: string;
  };
}

export const submitHelpRequest = async (
  payload: HelpRequestPayload,
  token: string
): Promise<{ success: boolean; message: string; ticketId?: string }> => {
  try {
    const response = await fetch(apiUrls.submitHelpRequest(), {
      method: "POST",
      headers: getAuthHeaders(token),
      body: JSON.stringify(payload)
    });

    const resData: HelpRequestResponse = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: resData.message || "Something went wrong. Please try again.",
        ticketId: resData.data?.ticket_id
      };
    }

    return {
      success: true,
      message: resData.message || "Request submitted successfully.",
      ticketId: resData.data?.ticket_id
    };
  } catch (error) {
    console.error("Network error submitting request:", error);
    return {
      success: false,
      message: "Something went wrong. Please try again."
    };
  }
};

interface CallStatusResponse {
  code: number;
  message: string;
  data: {
    call_status: CallStatusEnum;
  };
}

export const checkCallStatus = async (
  callId: string,
  token: string
): Promise<CallStatusEnum | null> => {
  try {
    const response = await fetch(apiUrls.callStatus(callId), {
      headers: getAuthHeaders(token)
    });

    if (!response.ok) {
      console.error("Failed to fetch call status");
      return null;
    }

    const result: CallStatusResponse = await response.json();
    return result.data.call_status;
  } catch (error) {
    const errMsg =
      error instanceof Error ? error.message : "Something went wrong";
    console.error("Error fetching call status:", errMsg);
    return null;
  }
};

export const declineCall = async (
  callId: string,
  userId: string,
  reason: string,
  token: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(
      apiUrls.decline({ callId: callId, userId: userId }),
      {
        method: "PATCH",
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          reason: reason
        })
      }
    );

    const resData = await response.json();
    
    if (!response.ok) {
      console.error(
        `Error ${response.status}: ${
          resData.message || "Failed to decline call"
        }`
      );
      return {
        success: false,
        message: resData.message || "Something went wrong. Please try again."
      };
    }

    return {
      success: true,
      message: resData.message || "Call declined successfully."
    };
  } catch (error) {
    console.error("Network error declining call:", error);
    return {
      success: false,
      message: "Something went wrong. Please try again."
    };
  }
};

export const rejectionReasonCall = async (
  callId: string,
  reason: string,
  token: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(apiUrls.rejectionReason({ callId: callId }), {
      method: "PATCH",
      headers: getAuthHeaders(token),
      body: JSON.stringify({
        reason: reason
      })
    });

    const resData = await response.json();

    if (!response.ok) {
      console.error(
        `Error ${response.status}: ${
          resData.message || "Failed to submit rejection reason"
        }`
      );
      return {
        success: false,
        message: resData.message || "Something went wrong. Please try again."
      };
    }

    return {
      success: true,
      message: resData.message || "Rejection Reason submitted successfully."
    };
  } catch (error) {
    console.error("Network error rejecting:", error);
    return {
      success: false,
      message: "Something went wrong. Please try again."
    };
  }
};
